import React, { useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import isEqual from 'lodash/isEqual'
import { useBreadcrumbs, BreadcrumbData } from './BreadcrumbsContext'
import { useLocation } from 'react-router-dom'

interface BreadcrumbProps {
  data: Omit<BreadcrumbData, 'id'>
  hidden?: boolean
  children?: React.ReactElement | null
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ data, hidden = false, children = null }) => {
  const { addCrumb, updateCrumb, removeCrumb, state } = useBreadcrumbs()
  const id = useRef(uuidv4())
  const prevData = useRef(data)
  const location = useLocation()

  useEffect(() => {
    // Initial mount or when hidden changes
    if (!hidden) {
      // Only add if this path isn't already in the breadcrumbs
      const existingCrumb = state.crumbs.find((crumb) => normalizePath(crumb.pathname) === normalizePath(data.pathname))

      if (!existingCrumb) {
        addCrumb(data)
      }
    } else {
      removeCrumb(id.current)
    }

    // Cleanup on unmount
    return () => {
      removeCrumb(id.current)
    }
  }, [hidden, location.pathname])

  useEffect(() => {
    // Only update if data actually changed
    if (!isEqual(data, prevData.current)) {
      updateCrumb({ id: id.current, ...data })
      prevData.current = data
    }
  }, [data, location.pathname])

  return children
}

// Helper function to normalize paths for comparison
const normalizePath = (path: string): string => {
  return '/' + path.replace(/^\/|\/$/g, '')
}

export default Breadcrumb
